/**
 * Modal — the one canonical modal shell for the portal.
 *
 * Wraps the global .modal-overlay + .modal.modal-{sm|md|lg|xl} classes
 * (apps/portal/src/styles/global.css) so a modal is "import the component",
 * not "remember the overlay + box + header + close + body + footer markup
 * and hope you used the right classes". Copied from the AlertDialog /
 * ContractShareModal shape — no bespoke overlay, no inline styles.
 *
 * Body content is `children`. Footer buttons (if any) go in `footer` and are
 * laid out by the canonical .modal-footer (right-aligned, gap, min-width btns).
 * Clicking the overlay closes by default; pass closeOnOverlay={false} for a
 * modal that must not be dismissed by a stray click (e.g. a wizard mid-step).
 */

import type { ReactNode } from 'react';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

interface ModalProps {
  /** Header title. Omit for a bare modal with no header bar. */
  title?: ReactNode;
  size?: ModalSize;
  /** Body content. */
  children: ReactNode;
  /** Footer buttons. Omit for no footer. */
  footer?: ReactNode;
  /** Close when the dark overlay is clicked. Default true. */
  closeOnOverlay?: boolean;
  /** Hide the × close button in the header. Default false. */
  hideClose?: boolean;
  /** Accessible label when there's no string title. */
  ariaLabel?: string;
  onClose: () => void;
}

export default function Modal({
  title,
  size = 'md',
  children,
  footer,
  closeOnOverlay = true,
  hideClose = false,
  ariaLabel,
  onClose,
}: ModalProps) {
  return (
    <div className="modal-overlay" onClick={closeOnOverlay ? onClose : undefined}>
      <div
        className={`modal modal-${size}`}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : ariaLabel}
        onClick={(e) => e.stopPropagation()}
      >
        {title !== undefined && (
          <div className="modal-header">
            <h2 className="m-0 text-base">{title}</h2>
            {!hideClose && (
              <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
                &times;
              </button>
            )}
          </div>
        )}
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );
}
